/**
 * FirstRunGuideCard — the parent's first-run activation guide.
 *
 * first-run-guide (T3). Three steps, all visible from the first render so the parent can see
 * the whole journey rather than one instruction at a time: add your child, start her session,
 * read her first report. Retires once for the family and never returns.
 *
 * WHY THIS EXISTS: today a parent registers, creates a child, and the trial banner unmounts,
 * leaving her with no next step at all. That is an activation failure, not a documentation one.
 *
 * ── Design rules that will otherwise be re-litigated ────────────────────────────────────────
 *
 * 1. PURELY PRESENTATIONAL. Every value arrives as a prop from the two queries the parent
 *    dashboard already makes (['parent','dashboard'] and ['parent','sessions']). This component
 *    adds NO network call and owns no fetching.
 *
 * 2. STEP PROGRESS IS DERIVED, NEVER STORED. One authority, so the guide and the underlying
 *    state cannot drift. There is no endpoint and no field for step progress; the only thing
 *    persisted anywhere is the collapse preference, and that lives in this browser.
 *
 * 3. ⚠️ step 2 MUST NOT be derived from `lastSessionStatus`. It is non-null for
 *    `ended_by_parent`, so a parent who starts a session and then ends it early would tick
 *    step 2 PERMANENTLY, lose the start button, and be advanced to a report that can never
 *    exist. Step 2 comes from a live session or from step 3, both of which can un-tick.
 *
 * 4. ⚠️ FAMILY LEVEL, ONE RUN PER FAMILY. Every signal below asks about the family, not a
 *    named child, which is what delivers "it must never come back": add a second child years
 *    later and the guide stays retired. Rewriting any of this per-child breaks that guarantee.
 *
 * 5. ⚠️ NEVER RENDER A HALF-ANSWERED GUIDE. Because nothing is stored, every step reads false
 *    until the dashboard data arrives. The parent dashboard only mounts this inside its
 *    already-loaded branch, so the guide cannot flash; the defensive `coinBalance` guard below
 *    is the second line of that. Without it, a parent who finished months ago would see the
 *    whole three-step guide flash up on every single visit and be told to start over.
 *
 * 6. ⚠️ "Out of sessions" gates on `isUnlimited`, NEVER on the balance alone — see the field
 *    docs on OtterCoinBalance. Unlimited families read {monthlyCoins: 0, isUnlimited: true}
 *    permanently, so a naive zero-check offers a top-up to the highest-paying customers.
 *
 * 7. Step 3 ticks on session COMPLETION, not on report availability. Reports lag. A parent
 *    whose child just finished forty minutes of work must not drop back to "start a session",
 *    which would read as "that did not count" at the exact conversion moment.
 */

import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ViewFullReportButton } from '@/components/ViewFullReportButton';
import type { ChildSummary, EngagementSummary, LiveSession, OtterCoinBalance } from '@/types/coins';

/** localStorage key for the collapse preference. Browser-only by design (see rule 2). */
const COLLAPSE_KEY = 'thinkotter.firstRunGuide.collapsed';

/**
 * localStorage key for "I have finished with this card".
 *
 * Browser-only, for the same reason as the collapse preference: step progress is DERIVED and
 * nothing about it is stored, so a dismissal must not become the first stored fact about a
 * family's progress — that is the thing that can go stale and start disagreeing with reality.
 *
 * The honest signal for "she has taken the payoff" would be her opening the report, but that
 * is server state we deliberately do not keep. So she tells us instead, which is also the
 * behaviour she expects from an X.
 *
 * Cost, accepted knowingly: it is per browser. Dismiss on a laptop and the card is still there
 * on a phone until the grace period clears it. For a card a family sees once, that is the right
 * trade against storing progress.
 */
const DISMISSED_KEY = 'thinkotter.firstRunGuide.dismissed';

/**
 * How long after a child finishes before the card retires even if no report ever appeared.
 * A safety net so the card cannot outlive its job when the report job fails.
 */
const REPORT_GRACE_MS = 24 * 60 * 60 * 1000;

export interface FirstRunGuideCardProps {
  /** The family's children (from the dashboard payload). Step 1 asks only whether this is non-empty. */
  children: ChildSummary[];
  /** All live English sessions across the family. Step 2 asks only whether this is non-empty. */
  liveSessions: LiveSession[];
  /** Engagement metrics. `childrenWithActivity` drives step 3; undefined is treated as 0. */
  engagementSummary: EngagementSummary | undefined;
  /** The family wallet. Undefined means the dashboard has not resolved — the card renders nothing. */
  coinBalance: OtterCoinBalance | undefined;
  /**
   * The child's first completed session, if any — resolved by the page from the sessions feed
   * it already loads. `reportAvailable` decides whether step 3 offers the report or a pending line.
   */
  firstCompletedSession?: { sessionId: string; reportAvailable: boolean } | null;
  /**
   * ISO 8601 time that session COMPLETED, and the server's current time.
   *
   * ⚠️ Both are pending on the backend and are deliberately optional until they land. Every date
   * on the payload today is a CREATION time, and measuring the grace period from when a session
   * STARTED would retire the card the instant a weekend-long session finished, taking the first
   * report with it — the guard becoming the failure it was added to prevent. Until these arrive
   * the grace period simply does not fire, which errs towards the card lingering rather than
   * vanishing early. Never substitute a start time here, and never use the device clock.
   */
  firstCompletedAt?: string | null;
  serverNow?: string | null;

  onAddChild: () => void;
  onStartSession: (child: ChildSummary) => void;
  onOpenLiveSession: (sessionId: string) => void;
  onSeePlans: () => void;
  /** True while a start request is in flight. */
  isStarting?: boolean;
  /** Surfaced verbatim beneath the action; never swallowed (TO-LL-036). */
  startError?: string | null;
}

type StepState = 'done' | 'current' | 'todo';

export function FirstRunGuideCard({
  children,
  liveSessions,
  engagementSummary,
  coinBalance,
  firstCompletedSession,
  firstCompletedAt,
  serverNow,
  onAddChild,
  onStartSession,
  onOpenLiveSession,
  onSeePlans,
  isStarting = false,
  startError = null,
}: FirstRunGuideCardProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // Storage disabled: the card still closes for this visit, and the grace period
      // below remains the backstop. Never let a preference write break the guide.
    }
    setDismissed(true);
  }, []);

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      // Private browsing / storage disabled: default to expanded. A first-run guide that
      // opens is strictly better than one that fails to render.
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // Preference is a nicety — never let it break the guide.
      }
      return next;
    });
  }, []);

  // Rule 5: never render a half-answered guide.
  if (!coinBalance) return null;

  // She has closed it. Nothing else needs checking.
  if (dismissed) return null;

  // ── Derived step state (rules 2, 3, 4) ────────────────────────────────────────────────────
  const step1Done = children.length > 0;
  const step3Done = (engagementSummary?.childrenWithActivity ?? 0) > 0;
  // Deliberately NOT "a session was started at some point" — see rule 3.
  const step2Done = liveSessions.length > 0 || step3Done;

  // ── Retirement ────────────────────────────────────────────────────────────────────────────
  // ⚠️ Retirement is keyed on the GRACE PERIOD ONLY, never on the report becoming available.
  //
  // The council's shorthand was "retire once the report is available", and taken literally that
  // is self-defeating: the report link lives on step 3 of this very card, so retiring the moment
  // the report exists means the parent NEVER SEES IT. The card would vanish at the exact instant
  // it was supposed to hand over the payoff. (Caught by the "spread across days" test below.)
  //
  // So: once her child finishes, the card stays and shows the report the moment it lands. It
  // then retires a day after COMPLETION whether or not the report ever arrived, which is what
  // stops it lingering. She gets the payoff AND the card cannot outlive its job.
  //
  // Until the backend surfaces a completion time this cannot fire at all, so the card lingers
  // rather than vanishing early — deliberately the safe direction. NEVER substitute a start
  // time here; see the prop docs for why that inverts the guard into the bug it prevents.
  const reportReady = !!firstCompletedSession?.reportAvailable;
  const graceExpired =
    !!firstCompletedAt &&
    !!serverNow &&
    new Date(serverNow).getTime() - new Date(firstCompletedAt).getTime() > REPORT_GRACE_MS;

  if (step1Done && step2Done && step3Done && graceExpired) return null;

  // ── Can she actually start? (rule 6) ──────────────────────────────────────────────────────
  const outOfSessions =
    !coinBalance.isUnlimited && coinBalance.monthlyCoins + coinBalance.topupCoins === 0;

  const firstChild = children[0] ?? null;
  const childName = firstChild?.displayName ?? 'your child';
  const liveSession = liveSessions[0] ?? null;

  const stepState = (done: boolean, isCurrent: boolean): StepState =>
    done ? 'done' : isCurrent ? 'current' : 'todo';

  const s1 = stepState(step1Done, !step1Done);
  const s2 = stepState(step2Done, step1Done && !step2Done);
  const s3 = stepState(step3Done, step2Done && !step3Done);

  const remaining = [step1Done, step2Done, step3Done].filter((d) => !d).length;
  const allDone = remaining === 0;

  return (
    <section
      aria-labelledby="first-run-guide-title"
      className="mb-6 rounded-3xl border border-otter-sand bg-white p-5 shadow-md"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2
            id="first-run-guide-title"
            className="font-display text-lg font-semibold text-otter-earth-800"
          >
            Getting started
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {remaining === 0
              ? "That's everything. Nice work."
              : remaining === 3
                ? "Three steps to your child's first session."
                : `${remaining} ${remaining === 1 ? 'step' : 'steps'} to go before ${childName}'s first report.`}
          </p>
        </div>
        {/*
          ONE control, and what it does changes with the state of the journey.

          While there is still something to do it collapses, because she may want the card out
          of the way without losing her place. Once all three are done there is nothing left to
          come back to, so collapsing is meaningless and the honest control is a close (Wayne,
          2026-07-27: "more intuitive to replace the collapse button with an 'x' to close button
          once all 3 are done").

          Closing is permanent for this browser. The grace period below stays as the backstop
          for anyone who never presses it.
        */}
        <button
          type="button"
          onClick={allDone ? dismiss : toggleCollapsed}
          {...(allDone ? {} : { 'aria-expanded': !collapsed, 'aria-controls': 'first-run-guide-steps' })}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-otter-sand text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
        >
          {allDone ? (
            <X className="h-4 w-4" aria-hidden="true" />
          ) : collapsed ? (
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="sr-only">
            {allDone
              ? 'Close getting started'
              : collapsed
                ? 'Show the getting started steps'
                : 'Hide the getting started steps'}
          </span>
        </button>
      </div>

      {!collapsed && (
        <ol id="first-run-guide-steps" className="mt-3">
          <GuideStep
            index={1}
            state={s1}
            title="Add your child"
            detail={
              step1Done
                ? `${childName} is ready to go.`
                : 'Give your child a name and a password they will remember.'
            }
          >
            {!step1Done && (
              <Button variant="warm" onClick={onAddChild} className="mt-2 h-11 font-bold">
                Add your child
              </Button>
            )}
          </GuideStep>

          <GuideStep
            index={2}
            state={s2}
            title={step1Done ? `Start ${childName}'s practice` : 'Start the first practice'}
            detail={
              step2Done
                ? liveSession
                  ? `${liveSession.childDisplayName} is working on it now.`
                  : 'Done.'
                : outOfSessions
                  ? `You have no sessions left. Add some and ${childName} can begin straight away.`
                  : 'Sit with your child, then tap when they are ready. It opens here, you stay signed in.'
            }
          >
            {step1Done && !step3Done && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {liveSession ? (
                  <Button
                    variant="warm"
                    onClick={() => onOpenLiveSession(liveSession.sessionId)}
                    className="h-11 font-bold"
                  >
                    Go to {liveSession.childDisplayName}&apos;s practice
                  </Button>
                ) : outOfSessions ? (
                  <Button variant="warm" onClick={onSeePlans} className="h-11 font-bold">
                    See plans and top ups
                  </Button>
                ) : (
                  firstChild && (
                    <Button
                      variant="warm"
                      onClick={() => onStartSession(firstChild)}
                      disabled={isStarting}
                      className="h-11 font-bold"
                    >
                      {isStarting ? 'Starting…' : `Start ${childName}'s practice`}
                    </Button>
                  )
                )}
              </div>
            )}
            {startError && (
              <p className="mt-2 text-xs text-red-600" role="alert">
                {startError}
              </p>
            )}
          </GuideStep>

          <GuideStep
            index={3}
            state={s3}
            title={step1Done ? `Read ${childName}'s first report` : 'Read the first report'}
            detail={
              step3Done
                ? reportReady
                  ? `See how it went and where ${childName} needs you.`
                  : `${childName} has finished. The report is being prepared.`
                : 'See how it went and where your child needs you.'
            }
            isLast
          >
            {step3Done &&
              (reportReady && firstCompletedSession ? (
                /*
                  ⚠️ Reuses ViewFullReportButton rather than navigating.
                  The report is a presigned URL opened in a new tab, NOT a route. An earlier
                  version of this navigated to /session/{id}, which took the parent to the
                  transcript of the session she had just watched instead of the report — the
                  payoff this entire card exists to deliver. Found by Wayne in UAT.
                  Do not replace this with a navigate().
                */
                <ViewFullReportButton
                  sessionId={firstCompletedSession.sessionId}
                  label={`Read ${childName}'s report`}
                />
              ) : (
                <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-pebbles-500">
                  <span
                    aria-hidden="true"
                    className="h-3 w-3 animate-spin rounded-full border-2 border-otter-sand border-t-otter-orange-500 motion-reduce:animate-none"
                  />
                  Preparing the report
                </p>
              ))}
          </GuideStep>
        </ol>
      )}
    </section>
  );
}

interface GuideStepProps {
  index: number;
  state: StepState;
  title: string;
  detail: string;
  isLast?: boolean;
  children?: React.ReactNode;
}

/**
 * One row of the guide. State is conveyed by the marker's SHAPE and by assistive text, never
 * by colour alone (WCAG 1.4.1).
 */
function GuideStep({ index, state, title, detail, isLast = false, children }: GuideStepProps) {
  const done = state === 'done';
  const muted = state === 'todo';

  return (
    <li
      className={cn(
        'grid grid-cols-[28px_1fr] gap-3 py-3',
        !isLast && 'border-b border-otter-sand'
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'font-display mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-extrabold',
          done && 'bg-student-500 text-white',
          state === 'current' && 'bg-otter-orange-500 text-white',
          muted && 'border-2 border-otter-sand bg-white text-pebbles-500'
        )}
      >
        {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : index}
      </span>
      <div>
        <p className={cn('text-sm font-bold', muted ? 'text-gray-400' : 'text-otter-earth-800')}>
          <span className="sr-only">
            {done ? 'Completed. ' : state === 'current' ? 'Current step. ' : 'Not started. '}
          </span>
          {title}
        </p>
        <p className={cn('mt-0.5 text-xs', muted ? 'text-gray-400' : 'text-gray-500')}>{detail}</p>
        {children}
      </div>
    </li>
  );
}
